import {
  INDUSTRY_TEXT_MAX_LENGTH,
  isAcceptedPreferenceText,
  NAME_MAX_LENGTH,
  SALUTATION_TEXT_MAX_LENGTH,
  WORK_AREA_MAX_LENGTH,
  type AddressForm,
  type ConversationPreferences,
  type ConversationPreferencesPatch,
  type IndustryCode,
  type Salutation,
} from 'features/conversation-preferences/services/conversation-preferences-service';

/** The card's fields as typed; an empty string is "nothing chosen". */
export type PersonalValues = {
  addressForm: AddressForm | '';
  salutation: Salutation | '';
  salutationText: string;
  givenName: string;
  familyName: string;
  industry: IndustryCode | '';
  industryText: string;
  workArea: string;
};

export type PersonalTextField =
  | 'salutationText'
  | 'givenName'
  | 'familyName'
  | 'industryText'
  | 'workArea';
export type PersonalFieldError = 'required' | 'tooLong' | 'invalid';
export type PersonalErrors = Partial<Record<PersonalTextField, PersonalFieldError>>;

export const PERSONAL_MAX_LENGTH: Record<PersonalTextField, number> = {
  salutationText: SALUTATION_TEXT_MAX_LENGTH,
  givenName: NAME_MAX_LENGTH,
  familyName: NAME_MAX_LENGTH,
  industryText: INDUSTRY_TEXT_MAX_LENGTH,
  workArea: WORK_AREA_MAX_LENGTH,
};

/** The saved preference; names fall back to the account (IAM) names while it has none. */
export function personalValues(
  preferences: ConversationPreferences,
  account?: { firstName?: string | null; lastName?: string | null }
): PersonalValues {
  return {
    addressForm: preferences.addressForm === 'unknown' ? '' : (preferences.addressForm ?? ''),
    salutation: preferences.salutation ?? '',
    salutationText: preferences.salutationText ?? '',
    givenName: preferences.givenName ?? account?.firstName ?? '',
    familyName: preferences.familyName ?? account?.lastName ?? '',
    industry: preferences.industry ?? '',
    industryText: preferences.industryText ?? '',
    workArea: preferences.workArea ?? '',
  };
}

const checkText = (value: string, field: PersonalTextField, required = false) => {
  if (!value.trim()) return required ? 'required' : undefined;
  if (value.trim().length > PERSONAL_MAX_LENGTH[field]) return 'tooLong';
  // The backend refuses `<>`, the backtick and control or invisible characters (SPEC26).
  return isAcceptedPreferenceText(value, PERSONAL_MAX_LENGTH[field]) ? undefined : 'invalid';
};

/** What would block saving; checked before anything is sent (C7). */
export function validatePersonalValues(values: PersonalValues): PersonalErrors {
  const errors: PersonalErrors = {
    salutationText:
      values.salutation === 'custom'
        ? checkText(values.salutationText, 'salutationText', true)
        : undefined,
    givenName: checkText(values.givenName, 'givenName'),
    familyName: checkText(values.familyName, 'familyName'),
    industryText:
      values.industry === 'other'
        ? checkText(values.industryText, 'industryText', true)
        : undefined,
    workArea: checkText(values.workArea, 'workArea'),
  };
  return Object.fromEntries(Object.entries(errors).filter(([, error]) => error));
}

const sameText = (a: string, b: string) => a.trim() === b.trim();
const textOrNull = (value: string) => value.trim() || null;

/**
 * Only the fields that changed (H5). A salutation travels with its text and an industry with its
 * text, which are cleared unless "custom" / "other" (H3, H4). The form is left out where the UI
 * has no du/Sie (H6).
 */
export function personalPatch(
  saved: PersonalValues,
  values: PersonalValues,
  { withAddressForm }: { withAddressForm: boolean }
): ConversationPreferencesPatch {
  const patch: ConversationPreferencesPatch = {};
  if (withAddressForm && values.addressForm && values.addressForm !== saved.addressForm) {
    patch.addressForm = values.addressForm;
  }
  if (
    values.salutation &&
    (values.salutation !== saved.salutation ||
      (values.salutation === 'custom' && !sameText(values.salutationText, saved.salutationText)))
  ) {
    patch.salutation = values.salutation;
    patch.salutationText =
      values.salutation === 'custom' ? textOrNull(values.salutationText) : null;
  }
  if (!sameText(values.givenName, saved.givenName)) patch.givenName = textOrNull(values.givenName);
  if (!sameText(values.familyName, saved.familyName)) {
    patch.familyName = textOrNull(values.familyName);
  }
  if (
    values.industry &&
    (values.industry !== saved.industry ||
      (values.industry === 'other' && !sameText(values.industryText, saved.industryText)))
  ) {
    patch.industry = values.industry;
    patch.industryText = values.industry === 'other' ? textOrNull(values.industryText) : null;
  }
  if (!sameText(values.workArea, saved.workArea)) patch.workArea = textOrNull(values.workArea);
  return patch;
}

/** Fields rebased as one: a choice and the text that belongs to it. */
const REBASE_GROUPS: (keyof PersonalValues)[][] = [
  ['addressForm'],
  ['salutation', 'salutationText'],
  ['givenName'],
  ['familyName'],
  ['industry', 'industryText'],
  ['workArea'],
];

/**
 * New server values under a form the user may be editing: every field group the user has not
 * touched (still equal to what was saved) takes the new values, and the user's edits stay. A
 * salutation and an industry move together with their text, so a background change never splits
 * them from an edit.
 */
export function rebasePersonalValues(
  saved: PersonalValues,
  server: PersonalValues,
  values: PersonalValues
): PersonalValues {
  const next = { ...values };
  REBASE_GROUPS.forEach((group) => {
    if (group.every((field) => values[field] === saved[field])) {
      group.forEach((field) => Object.assign(next, { [field]: server[field] }));
    }
  });
  return next;
}
